import { Drawer, DrawerContent, Portal } from '@chakra-ui/react';
import { Modal, ModalBody, ModalContent, ModalOverlay } from '@chakra-ui/react';
import {
  SearchDataItemProps,
  SearchDataProps,
  SearchDialog,
} from '@sketch-note/design-system/src/components/search-dialog';
import classnames from 'classnames';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';
import { API, endpoints, useSocketNotification } from 'src/api';
import Analytics from 'src/api/services/analytics/analytics';
import { useIntercomService } from 'src/api/services/intercom/intercom-service';
import { CommonNavbar, WorkspaceNavbar } from 'src/components/navbar';
import { QuickHelp } from 'src/components/quick-help';
import { Settings } from 'src/components/settings';
import { Member } from 'src/components/workspaces';
import { Notifications } from 'src/features/notifications';
import { useAuth } from 'src/shared/hooks/use-auth';
import { useLayout } from 'src/shared/hooks/use-layout';

export interface MainLayoutProps {
  children: JSX.Element | JSX.Element[];
  sidebar?: JSX.Element;
  isOverView?: boolean;
  workspaceName?: string;
  workspaceMembers?: Member[];
  handleWorkspaceSettingsModal?: (value?: string, index?: number) => void;
}

export const MainLayout = ({
  children,
  sidebar,
  workspaceName,
  workspaceMembers,
  handleWorkspaceSettingsModal,
  isOverView = false,
}: MainLayoutProps): JSX.Element => {
  const [searchFilterValue, setSearchFilterValue] = useState({
    label: 'All results',
    value: 'all-results',
  });

  const router = useRouter();

  const {
    currentSettingsMenu,
    isHelpOpened,
    isNotificationsOpened,
    isSettingsOpened,
    isSidebarOpened,
    isSearchOpen,
    onChangeOrganisation,
    onCloseHelp,
    onCloseNotifications,
    onCloseSettings,
    onOpenHelp,
    onSearchOpen,
    notificationCount,
    onOpenNotificationSettings,
    onOpenNotifications,
    onOpenSettings,
    onProfileMenu,
    onToggleSideBar,
    onSearchClose,
    onFetchNotificationCount,
  } = useLayout();

  useIntercomService();

  const authData = useAuth((s) => s.userDetails);
  const fetchUserDetails = useAuth((s) => s.fetchUserDetails);
  const socketNotification = useSocketNotification();

  useEffect(() => {
    fetchUserDetails();
  }, [socketNotification]);

  useEffect(() => {
    if (authData?.authentication === 'authorised') {
      onFetchNotificationCount();
    }
  }, [authData?.authentication]);

  const orgId = router?.query?.orgId;
  const mode = router?.query?.mode;
  const getSearchResult = async (
    search: string,
    start: number,
    limit: number
  ): Promise<SearchDataProps> => {
    Analytics.track('Lesson Search', {
      'Search Word': search,
      'Search Source': 'Main Search Tab',
    });
    const result =
      router.query.mode === 'a'
        ? await API.get(`${endpoints.search.url}?search=${search}&start=${start}&limit=${limit}`)
        : await API.get(
            `${endpoints.search.filesearchurl}?search=${search}&start=${start}&limit=${limit}`
          );

    const retval = result.data;
    return retval;
  };

  const handleProgressSorting = (e) => {
    setSearchFilterValue({ label: e.label, value: e.value });
  };

  const handleSelectSearch = (item: SearchDataItemProps, postSearch: boolean) => {
    if (postSearch) {
      router.query.mode === 'a'
        ? API.post(endpoints.search.save, item)
        : API.post(endpoints.search.filesearchsave, item);
    }
    if (router.query.mode === 'a') {
      router.push(`/${mode}/${orgId}/lessons/${item.slug}`);
    } else {
      router.push(`/${mode}/${orgId}/files/${item.id}`);
    }
  };

  const getRecentSearches = async (): Promise<SearchDataItemProps[]> => {
    const result =
      router.query.mode === 'a'
        ? await API.get(`${endpoints.search.recent}`)
        : await API.get(`${endpoints.search.filesearchrecent}`);
    return result.data;
  };

  return (
    <div
      className={classnames('', {
        'overflow-hidden flex': !isOverView,
      })}
    >
      {sidebar ? (
        <div
          className={classnames({ 'h-screen': !isOverView, 'fixed h-full': isOverView })}
          style={{ width: isSidebarOpened && !isOverView ? '256px' : undefined, zIndex: '15' }}
        >
          {sidebar}
        </div>
      ) : (
        <div />
      )}

      <div
        className={classnames('min-w-0 flex-grow', {
          'flex-1 ml-256': isSidebarOpened && isOverView,
          'w-full h-screen': !isOverView,
        })}
      >
        <div
          className={classnames({
            'z-20 bg-white h-50 ': true,
            'top-0 sticky': isOverView,
          })}
        >
          <CommonNavbar
            handleToggleSideBar={onToggleSideBar}
            wrapperClassName="relative top-0"
            username={authData?.name || ''}
            notificationCount={notificationCount}
            isSideBarOpen={isSidebarOpened}
            onNotificationClick={onOpenNotifications}
            onHelp={onOpenHelp}
            searchPlaceholder="Search..."
            onProfileMenuClick={onProfileMenu}
            onOrganisationClick={onChangeOrganisation}
            onSearchOpen={onSearchOpen}
            authData={authData}
          />
        </div>
        {router.query.workspaceId && router.pathname.includes('files') ? (
          <WorkspaceNavbar />
        ) : (
          <div />
        )}

        <Modal isOpen={isSearchOpen} onClose={onSearchClose}>
          <ModalOverlay />
          <ModalContent
            maxWidth="70rem"
            marginTop="20rem"
            borderRadius="0.8rem"
            className="rounded-lg"
          >
            <ModalBody className="flex items-center justify-center text-left" padding="0px">
              <SearchDialog
                getRecentSearches={getRecentSearches}
                getSearchResult={getSearchResult}
                onClose={onSearchClose}
                sortMenuOptions={[
                  { label: 'Best Matches', value: 'best-matches' },
                  { label: 'Last edited : Newest first', value: 'newest-update-first' },
                  { label: 'Last edited : Oldest first', value: 'oldest-update-first' },
                  { label: 'Created : Newest first', value: 'newest-created-first' },
                  { label: 'Created : Oldest first', value: 'oldest-created-first' },
                ]}
                filterMenuOptions={[
                  { label: 'All results', value: 'all-results' },
                  { label: 'Lessons', value: 'lessons' },
                  { label: 'Creations', value: 'creations' },
                  { label: 'Catalog', value: 'catalog' },
                  { label: 'Docs', value: 'docs' },
                  { label: 'Kanban', value: 'kanban' },
                  { label: 'Calendar', value: 'calendar' },
                ]}
                sortTxt="Sort"
                filterText=""
                activeSortValue={{ label: '', value: '' }}
                activeFilterValue={{
                  label: searchFilterValue.label,
                  value: searchFilterValue.value,
                }}
                onClickSortValue={handleProgressSorting}
                onSelectSearchResult={handleSelectSearch}
              />
            </ModalBody>
          </ModalContent>
        </Modal>

        <Drawer
          size="lg"
          isOpen={isNotificationsOpened}
          placement="right"
          onClose={onCloseNotifications}
        >
          <Portal>
            <DrawerContent className="overflow-auto">
              {authData?.authentication === 'authorised' ? <Notifications /> : <div />}
            </DrawerContent>
          </Portal>
        </Drawer>

        <Modal isOpen={isSettingsOpened} onClose={onCloseSettings}>
          <ModalOverlay />
          <ModalContent
            width="auto"
            maxWidth="auto"
            margin="auto"
            borderRadius="0.8rem"
            className="static rounded-lg"
          >
            <ModalBody className="flex items-center justify-center text-left" padding="0px">
              <Settings
                currentMenu={currentSettingsMenu}
                isOpen={isSettingsOpened}
                onClose={onCloseSettings}
              />
            </ModalBody>
          </ModalContent>
        </Modal>

        <Drawer size="lg" isOpen={isHelpOpened} placement="right" onClose={onCloseHelp}>
          <DrawerContent className="overflow-auto">
            <QuickHelp onClose={onCloseHelp} onClickReset={onOpenSettings} />
          </DrawerContent>
        </Drawer>

        <div
          style={{ height: !isOverView ? 'calc(100% - 50px)' : undefined }}
          className="overflow-auto"
        >
          {children}
        </div>
      </div>
    </div>
  );
};
